'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Collapsible } from './collapsible';
import type { ControlDensity, ControlSize } from './control-styles';

/** Shared disclosure with compact popover placement; portal targets stay mounted. */
export function MoreControls({
  children,
  label = 'More',
  size = 'toolbar',
  density,
}: {
  children: ReactNode;
  label?: string;
  size?: ControlSize;
  density?: ControlDensity;
}) {
  const disclosure = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !disclosure.current?.contains(event.target) &&
        !document.querySelector('[role="dialog"][aria-modal="true"]')
      )
        setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, []);
  useEffect(() => {
    if (open)
      disclosure.current
        ?.querySelector<HTMLElement>(
          '[data-more-actions] a[href], [data-more-actions] button:not([disabled])',
        )
        ?.focus();
  }, [open]);
  return (
    <Collapsible
      ref={disclosure}
      label={label}
      open={open}
      onOpenChange={setOpen}
      className="relative border-0 bg-transparent px-0"
      triggerVariant="outline"
      size={size}
      density={density}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !event.defaultPrevented) {
          event.preventDefault();
          setOpen(false);
          disclosure.current?.querySelector('button')?.focus();
        }
      }}
      triggerClassName="w-auto shrink-0 whitespace-nowrap"
      contentClassName="absolute right-0 z-40 mt-2 max-h-[70vh] w-64 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-overlay border border-border bg-surface-sheet p-1 shadow-popover"
    >
      <div
        data-more-actions
        aria-label={label}
        className="flex flex-col gap-1 [&_a]:w-full [&_a]:justify-start [&_button]:w-full [&_button]:justify-start"
      >
        {children}
      </div>
    </Collapsible>
  );
}
